import type * as React from 'react';
import type { UseFormRegister, UseFormSetValue } from 'react-hook-form';

import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';

import type { InvoiceRow } from '../types';
import { applyParentInheritance } from './adjustment-note-defaults';
import type { AdjustmentNoteSubmitValues } from './adjustment-note-schema';

// DEPRECATED: the client-supplied parent picker. The primary way to raise a note
// is now the per-invoice "Create next" action, where the predecessor is derived
// server-side (by construction) and can't drift to the wrong invoice. This picker
// survives only inside the BL-level adjustment dialog, which still covers the
// itemised sell-side cases the convenience path doesn't collect; it is slated for
// removal once that flow migrates onto the by-construction path.
export interface ParentInvoicePickerProps {
  readonly parents: InvoiceRow[];
  readonly loading: boolean;
  readonly editing: boolean;
  readonly register: UseFormRegister<AdjustmentNoteSubmitValues>;
  readonly setValue: UseFormSetValue<AdjustmentNoteSubmitValues>;
  readonly side: 'buy' | 'sell';
  readonly disabled: boolean;
  // Follow-on creates fix the parent to the invoice the action was launched
  // from — the field shows which document is adjusted but can't be changed.
  readonly locked: boolean;
}

// An order-level advance (bl_order_id null) covering this BL is offered as a
// parent only when the advance-tracking reads are on; badge it so an operator
// tells the full advance apart from a same-BL Provisional/Final at a glance.
function isOrderLevelAdvance(row: InvoiceRow): boolean {
  return row.invoice_type === 'Advance' && row.bl_order_id === null;
}

function parentOptionLabel(row: InvoiceRow): string {
  const type = row.invoice_type ?? '—';
  const number = row.invoice_number ?? `#${row.id}`;
  const amount = row.total_amount ?? '0';
  const issued = row.issue_date !== null ? ` · issued ${row.issue_date.slice(0, 10)}` : '';
  const badge = isOrderLevelAdvance(row) ? ' · order-level' : '';
  return `${type} · ${number} · ${row.currency ?? ''} ${amount}${issued}${badge}`;
}

export function ParentInvoicePicker(props: ParentInvoicePickerProps) {
  if (!props.editing && !props.loading && props.parents.length === 0) {
    return (
      <div
        data-testid="adjustment-no-parents"
        className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm"
      >
        No eligible parent invoices on this side. A note can reference any issued invoice
        (Provisional, Advance, or Final Settlement) — drafts, void or cancelled invoices, and other
        notes are excluded. Issue an invoice first before raising an adjustment.
      </div>
    );
  }
  const reg = props.register('adjusts_invoice_id', {
    onChange: (e: React.ChangeEvent<HTMLSelectElement>) => {
      const picked = props.parents.find((row) => row.id === e.target.value) ?? null;
      applyParentInheritance(picked, props.side, props.setValue);
    },
  });
  return (
    <div className="flex flex-col gap-1">
      <Label htmlFor="adjusts_invoice_id">Parent Invoice</Label>
      <Select
        id="adjusts_invoice_id"
        disabled={props.disabled || props.editing || props.locked}
        data-testid="adjustment-parent-picker"
        {...reg}
      >
        <option value="">{props.loading ? 'Loading…' : 'Select a parent invoice'}</option>
        {props.parents.map((row) => (
          <option key={row.id} value={row.id}>
            {parentOptionLabel(row)}
          </option>
        ))}
      </Select>
      {props.editing || props.locked ? (
        <p className="text-muted-foreground text-xs">
          {props.locked
            ? 'Fixed to the invoice this note was started from.'
            : 'Parent invoice is fixed after create. To change parents, clear the draft and start a new note.'}
        </p>
      ) : (
        <p className="text-muted-foreground text-xs" data-testid="adjustment-parent-eligibility">
          Only issued invoices on this side are listed — drafts, void or cancelled invoices, and
          other notes can&apos;t be adjusted.
        </p>
      )}
    </div>
  );
}
